// src/components/common/LandingPage.js
import React, { useState, useEffect, useRef } from 'react';
import EnhancedAuthModal from './EnhancedAuthModal';
import DevicesMap from './DevicesMap';
import {
  ArrowRight, CheckCircle, CloudRain,
  BarChart3, Database, Bell, Shield,
  Play, Pause, Volume2, VolumeX, Maximize2,
  Smartphone, Download, Share, PlusSquare, X,
} from 'lucide-react';
import { fetchWeatherDevices } from '../../services/weatherService';

/* ── Station Video Player ── */
const StationVideo = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [hasVideo, setHasVideo] = useState(true);
  const [showControls, setShowControls] = useState(false);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else          { v.pause(); setPlaying(false); }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(m => !m);
  };

  const openFullscreen = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitRequestFullscreen) v.webkitRequestFullscreen();
  };

  if (!hasVideo) {
    return (
      <div className="w-full rounded-xl border border-dashed border-gray-700 bg-gray-900/60 flex flex-col items-center justify-center px-4 text-center" style={{ minHeight: 'clamp(180px, 40vw, 420px)' }}>
        <div className="w-14 h-14 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center mb-4">
          <CloudRain className="w-7 h-7 text-gray-500" />
        </div>
        <p className="text-gray-300 text-sm font-semibold mb-1">Station video coming soon</p>
        <p className="text-gray-500 text-xs mb-4">Field deployment footage will appear here</p>
        <div className="bg-gray-800/80 border border-gray-700 rounded-lg px-4 py-2.5 text-left w-full max-w-xs">
          <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Drop your file here</p>
          <p className="text-green-400 font-mono text-[11px]">public/video/video.mp4</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative w-full rounded-xl border border-gray-700 bg-black overflow-hidden group"
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {/* Video */}
      <video
        ref={videoRef}
        src="/video/video.mp4"
        autoPlay
        muted
        loop
        playsInline
        className="w-full object-cover"
        style={{ minHeight: 'clamp(220px, 45vw, 460px)', maxHeight: '460px' }}
        onError={() => setHasVideo(false)}
      />

      {/* Top overlay: LIVE badge */}
      <div className="absolute top-2.5 left-2.5 flex items-center space-x-1.5 bg-black/60 backdrop-blur-sm rounded-full px-2.5 py-1">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
        <span className="text-white text-[10px] font-bold tracking-widest uppercase">Live Station</span>
      </div>

      {/* Bottom gradient + label */}
      <div className="absolute bottom-0 inset-x-0 h-14 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
      <div className="absolute bottom-2.5 left-3 right-3 flex items-end justify-between pointer-events-none">
        <p className="text-white/80 text-[10px]">Climate Pulse IoT Weather Station — Field Deployment</p>
      </div>

      {/* Controls — shown on hover */}
      <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
        <div className="flex items-center space-x-2 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1.5">
          <button onClick={togglePlay} className="text-white hover:text-green-400 transition-colors">
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button onClick={toggleMute} className="text-white hover:text-green-400 transition-colors">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <button onClick={openFullscreen} className="text-white hover:text-green-400 transition-colors">
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Network stats hook ── */
const useNetworkStats = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        // Use weatherService so status is computed from reading timestamps
        // (online ≤30 min, warning ≤2 hr, offline >2 hr) — not the raw DB 'active' flag
        const devices = await fetchWeatherDevices();
        const total   = devices.length;
        const online  = devices.filter(d => d.status === 'online').length;
        const warning = devices.filter(d => d.status === 'warning').length;
        const offline = devices.filter(d => d.status === 'offline').length;
        // Count unique first-word location prefixes as a rough "locations" count
        const locs = new Set(devices.map(d => (d.name || '').split(' ')[0]).filter(Boolean)).size;
        setStats({ total, online, warning, offline, locations: locs || 1 });
      } catch (_) {}
    })();
  }, []);
  return stats;
};

const FEATURES = [
  { icon: BarChart3, label: 'Real-Time Charts',   color: 'text-blue-400' },
  { icon: Database,  label: 'CSV / Excel Export', color: 'text-green-400' },
  { icon: Bell,      label: 'Smart Alerts',       color: 'text-orange-400' },
  { icon: Shield,    label: 'Role-Based Access',  color: 'text-purple-400' },
];

// APK download URL — put climate-pulse.apk in public/downloads/
const APK_URL = '/downloads/climate-pulse.apk';

// Detect device type
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isAndroid = () => /Android/.test(navigator.userAgent);
const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;

/* ── iOS Install Instructions Modal ── */
const IOSInstallModal = ({ onClose }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      padding: '0 12px 12px',
    }}
    onClick={onClose}
  >
    <div
      style={{
        background: '#1f2937', border: '1px solid #374151',
        borderRadius: 20, padding: '20px 20px 28px',
        width: '100%', maxWidth: 420,
        boxShadow: '0 -8px 40px rgba(0,0,0,0.6)',
      }}
      onClick={e => e.stopPropagation()}
    >
      {/* Handle bar */}
      <div style={{ width: 36, height: 4, background: '#4b5563', borderRadius: 2, margin: '0 auto 16px' }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/logo.png" alt="Climate Pulse" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain' }} />
          <div>
            <p style={{ margin: 0, color: '#f9fafb', fontWeight: 700, fontSize: 15 }}>Climate Pulse</p>
            <p style={{ margin: 0, color: '#6b7280', fontSize: 11 }}>Add to Home Screen</p>
          </div>
        </div>
        <button onClick={onClose} style={{ background: '#374151', border: 'none', borderRadius: 20, padding: '4px 8px', cursor: 'pointer', color: '#9ca3af' }}>
          <X style={{ width: 14, height: 14 }} />
        </button>
      </div>

      <p style={{ color: '#9ca3af', fontSize: 13, margin: '0 0 20px', lineHeight: 1.5 }}>
        Install Climate Pulse on your iPhone for the best experience — works offline and launches like a native app.
      </p>

      {/* Steps */}
      {[
        { icon: Share, step: '1', text: 'Tap the', highlight: 'Share', after: 'button in Safari\'s toolbar' },
        { icon: PlusSquare, step: '2', text: 'Scroll down and tap', highlight: 'Add to Home Screen' },
        { icon: CheckCircle, step: '3', text: 'Tap', highlight: 'Add', after: 'in the top-right corner' },
      ].map(({ icon: Icon, step, text, highlight, after }) => (
        <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: '#0d2137',
            border: '1px solid #1e3a5f', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icon style={{ width: 18, height: 18, color: '#38bdf8' }} />
          </div>
          <p style={{ margin: 0, color: '#d1d5db', fontSize: 13, lineHeight: 1.4 }}>
            {text} <strong style={{ color: '#f9fafb' }}>{highlight}</strong>{after ? ` ${after}` : ''}
          </p>
        </div>
      ))}

      {/* Safari note */}
      <div style={{ background: '#0d1117', border: '1px solid #1f2d3d', borderRadius: 10, padding: '10px 14px', marginTop: 4 }}>
        <p style={{ margin: 0, color: '#6b7280', fontSize: 11 }}>
          Make sure you're using <strong style={{ color: '#9ca3af' }}>Safari</strong> — this feature isn't available in Chrome or other browsers on iOS.
        </p>
      </div>
    </div>
  </div>
);

/* ── App Download Section ── */
const AppDownloadSection = () => {
  const [showIOSModal, setShowIOSModal] = useState(false);

  // Don't show if already running as installed PWA
  if (isStandalone()) return null;

  const ios     = isIOS();
  const android = isAndroid();

  return (
    <>
      <div style={{
        margin: '0 20px',
        background: 'linear-gradient(135deg, rgba(6,95,70,0.15) 0%, rgba(14,116,144,0.1) 100%)',
        border: '1px solid rgba(52,211,153,0.15)',
        borderRadius: 14, padding: '14px 16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Smartphone style={{ width: 14, height: 14, color: '#34d399', flexShrink: 0 }} />
          <p style={{ margin: 0, color: '#f9fafb', fontSize: 12, fontWeight: 700, letterSpacing: '0.03em' }}>
            Get the App
          </p>
        </div>

        <p style={{ margin: '0 0 12px', color: '#9ca3af', fontSize: 11, lineHeight: 1.5 }}>
          Access live weather data anywhere — offline-capable, fast, and native-like.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

          {/* Android — APK download or highlighted if on Android */}
          <a
            href={APK_URL}
            download="climate-pulse.apk"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: android ? '#065f46' : '#1f2937',
              border: `1px solid ${android ? '#10b981' : '#374151'}`,
              borderRadius: 10, padding: '10px 14px', textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            {/* Android robot icon (SVG inline) */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
              <path d="M17.523 15.341A5.5 5.5 0 0 0 17.5 14a5.5 5.5 0 0 0-11 0c0 .115.004.23.012.341A2 2 0 0 0 5 16v1a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-1a2 2 0 0 0-1.477-1.659z" fill={android ? '#34d399' : '#6b7280'} />
              <circle cx="9.5" cy="14" r="1" fill={android ? '#065f46' : '#374151'} />
              <circle cx="14.5" cy="14" r="1" fill={android ? '#065f46' : '#374151'} />
              <path d="M8 11.5l-1.5-3M16 11.5l1.5-3" stroke={android ? '#34d399' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round" />
              <path d="M9 9h6" stroke={android ? '#34d399' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: android ? '#ecfdf5' : '#d1d5db' }}>
                Download for Android
              </p>
              <p style={{ margin: 0, fontSize: 10, color: android ? '#6ee7b7' : '#6b7280' }}>
                APK · Direct install · v1.0
              </p>
            </div>
            <Download style={{ width: 14, height: 14, color: android ? '#34d399' : '#4b5563', flexShrink: 0 }} />
          </a>

          {/* iOS — Add to Home Screen prompt */}
          <button
            onClick={() => setShowIOSModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: ios ? '#0c1a2e' : '#1f2937',
              border: `1px solid ${ios ? '#0ea5e9' : '#374151'}`,
              borderRadius: 10, padding: '10px 14px', cursor: 'pointer',
            }}
          >
            {/* Apple icon (SVG) */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill={ios ? '#38bdf8' : '#6b7280'} style={{ flexShrink: 0 }}>
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: ios ? '#e0f2fe' : '#d1d5db' }}>
                Add to Home Screen
              </p>
              <p style={{ margin: 0, fontSize: 10, color: ios ? '#7dd3fc' : '#6b7280' }}>
                iOS · Safari · No App Store needed
              </p>
            </div>
            <Share style={{ width: 14, height: 14, color: ios ? '#38bdf8' : '#4b5563', flexShrink: 0 }} />
          </button>

          {/* Coming soon badges */}
          <div style={{ display: 'flex', gap: 6, paddingTop: 2 }}>
            {[
              { label: 'Google Play — Coming Soon' },
              { label: 'App Store — Coming Soon' },
            ].map(({ label }) => (
              <div key={label} style={{
                flex: 1, background: '#111827', border: '1px solid #1f2937',
                borderRadius: 8, padding: '5px 8px', textAlign: 'center',
              }}>
                <p style={{ margin: 0, color: '#4b5563', fontSize: 9, fontWeight: 600, letterSpacing: '0.02em' }}>{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showIOSModal && <IOSInstallModal onClose={() => setShowIOSModal(false)} />}
    </>
  );
};

/* ── Landing Page ── */
const LandingPage = ({ onLoginSuccess }) => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authInitialEmail, setAuthInitialEmail] = useState('');
  const [authInitialToken, setAuthInitialToken] = useState('');
  const stats = useNetworkStats();
  const openLogin    = () => { setAuthMode('login');    setShowAuthModal(true); };
  const openRegister = () => { setAuthMode('register'); setShowAuthModal(true); };

  // Handle ?setPassword=1&email=...&token=... from admin-created account email
  // Token is the OTP embedded in the link — skip OTP entry, go straight to set-password
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('setPassword') === '1') {
      const email = params.get('email') || '';
      const token = params.get('token') || '';
      setAuthInitialEmail(email);
      setAuthInitialToken(token);
      setAuthMode(token ? 'reset-password' : 'reset-otp');
      setShowAuthModal(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-gray-950">

      {/* ═══ LEFT PANEL ═══ */}
      <div className="relative z-20 flex flex-col w-full lg:w-[420px] xl:w-[460px] flex-shrink-0 bg-gray-950 h-full overflow-hidden">

        {/* Navbar */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-800/60 flex-shrink-0">
          <img src="/logo.png" alt="Climate Pulse" className="h-14 w-auto object-contain" />
          <div className="flex items-center space-x-2">
            <button onClick={openLogin} className="text-sm text-gray-400 hover:text-white transition-colors font-medium px-3 py-1.5">
              Sign in
            </button>
            <button onClick={openRegister} className="text-sm bg-blue-600 hover:bg-blue-500 text-white font-semibold px-3 py-1.5 rounded-lg transition-colors">
              Register
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto flex flex-col">

          {/* Station video */}
          <div className="px-5 pt-4 pb-3 flex-shrink-0">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Station Deployment</p>
            <StationVideo />
          </div>

          <div className="mx-5 border-t border-gray-800/60 flex-shrink-0" />

          {/* Hero */}
          <div className="px-5 pt-4 pb-3 flex-shrink-0">
            <h1 className="text-2xl xl:text-3xl font-extrabold text-white leading-tight mb-2">
              Climate Data<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">for Africa</span>
            </h1>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Real-time IoT weather data from stations across the continent.
              Track conditions, download records, and power your research.
            </p>

            {/* CTAs */}
            <div className="flex flex-col space-y-2">
              <button onClick={openRegister} className="flex items-center justify-center space-x-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-blue-600/25">
                <span>Get Started — It's Free</span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={openLogin} className="flex items-center justify-center px-5 py-2.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 hover:text-white rounded-xl font-medium text-sm transition-all">
                Sign In to Your Account
              </button>
            </div>
            <div className="flex items-center space-x-1.5 mt-2.5">
              <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
              <span className="text-gray-500 text-xs">No credit card required · Free tier available</span>
            </div>
          </div>

          <div className="mx-5 border-t border-gray-800/60 flex-shrink-0" />

          {/* Network stats */}
          <div className="px-5 pt-3 pb-3 flex-shrink-0">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Network Overview</p>
            <div className="grid grid-cols-4 gap-2">
              {[
                { label: 'Stations', value: stats?.total },
                { label: 'Online',   value: stats?.online,    accent: 'text-green-400' },
                { label: 'Warning',  value: stats?.warning,   accent: 'text-yellow-400' },
                { label: 'Offline',  value: stats?.offline,   accent: 'text-orange-400' },
              ].map(({ label, value, accent }) => (
                <div key={label} className="bg-gray-800/50 border border-gray-700/50 rounded-lg py-2.5 px-2 text-center">
                  {value == null
                    ? <div className="h-5 w-6 mx-auto bg-gray-700 rounded animate-pulse mb-1" />
                    : <div className={`text-base font-bold ${accent || 'text-white'}`}>{value}</div>
                  }
                  <div className="text-gray-500 text-[9px] mt-0.5 truncate">{label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mx-5 border-t border-gray-800/60 flex-shrink-0" />

          {/* Feature badges */}
          <div className="px-5 pt-3 pb-4 flex-shrink-0">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Platform Features</p>
            <div className="grid grid-cols-2 gap-2">
              {FEATURES.map(({ icon: Icon, label, color }) => (
                <div key={label} className="flex items-center space-x-2 bg-gray-800/40 border border-gray-700/40 rounded-lg px-3 py-2">
                  <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${color}`} />
                  <span className="text-gray-300 text-xs font-medium">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mx-5 border-t border-gray-800/60 flex-shrink-0" />

          {/* Get the App */}
          <div className="py-3 flex-shrink-0">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2.5 px-5">Mobile App</p>
            <AppDownloadSection />
          </div>

          {/* Spacer to push copyright to bottom if space allows */}
          <div className="flex-1" />

          {/* Footer copyright */}
          <div className="px-5 py-3 border-t border-gray-800/60 flex-shrink-0">
            <p className="text-gray-600 text-[10px] text-center">
              © {new Date().getFullYear()} Climate Pulse · Built for African climate resilience
            </p>
          </div>
        </div>
      </div>

      {/* ═══ RIGHT PANEL — MAP ═══ */}
      <div className="hidden lg:block flex-1 relative">
        <DevicesMap onLoginSuccess={onLoginSuccess} onStationSelect={() => {}} isPublicView={true} />
        <div className="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-gray-950 to-transparent pointer-events-none z-10" />
        <div className="absolute top-4 right-4 z-20 bg-gray-950/80 backdrop-blur-sm border border-gray-700 rounded-lg px-3 py-2">
          <p className="text-xs text-gray-300 font-medium">Live Station Network</p>
          <p className="text-xs text-gray-500">Click a station to preview</p>
        </div>
      </div>

      {/* Mobile — faint map backdrop */}
      <div className="lg:hidden absolute inset-0 z-0 opacity-10 pointer-events-none bg-gradient-to-br from-emerald-900 via-teal-900 to-gray-950" />

      {showAuthModal && (
        <EnhancedAuthModal
          isOpen={showAuthModal}
          initialMode={authMode}
          initialEmail={authInitialEmail}
          initialToken={authInitialToken}
          onClose={() => setShowAuthModal(false)}
          onLoginSuccess={(u) => { setShowAuthModal(false); onLoginSuccess(u); }}
        />
      )}
    </div>
  );
};

export default LandingPage;
